import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message'
import { configSet } from './config_set'
import { EXEDriver } from '../exe/driver.exe'
import { DeviceManager, DeviceProtocol } from '../device'
import { WebDriver } from '../web/driver.web'
import { ResDeviceState, UsingDeviceData } from '@libs/types/response'
import { DevicePid, KK, VK, kks } from '@/define/define'
import firmware from '@/upgrade/firmware'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { calibrationHandler } from './calibration_set'
import { HelperService } from '../helper/helper'
import { HelperSocketMsgType } from '../helper/message'
import { STORAGE_KEYS } from '@libs/define/storage_key'
import { setOrRead } from './set_read'
import { SpecificItem } from '@libs/types/enum'
import { operate } from './operate'

export class DriverService {
  static instance = new DriverService()
  private webDriver = new WebDriver()
  exeDriver = new EXEDriver()

  private get getSocketService() {
    return DeviceManager.instance.state.driverProtocol == DeviceProtocol.dll_exe ? this.exeDriver.socketService : this.webDriver.socketService
  }

  askType<U>(t: DriverSocketMsgType | HelperSocketMsgType): Promise<U> {
    return this.getSocketService.askType<U>(t)
  }
  askTypeAndData<U>(msg: DriverSocketMsg<any>): Promise<U> {
    return this.getSocketService.askTypeAndData<U>(msg)
  }
  setHandler(messageType: DriverSocketMsgType | HelperSocketMsgType, onSuccess: (data: any) => void, onFailure?: (error: any) => void) {
    return this.getSocketService.setHandler(messageType, onSuccess, onFailure)
  }

  async initializeDevice() {
    try {
      const res = await this.getSocketService.askType<UsingDeviceData>(DriverSocketMsgType.MTUsingDevice)
      if (res) {
        // 键位映射必须在 initCalibration 之前完成——initCalibration 设置响应式状态后
        // Vue 会在下一个微任务挂载校准组件并启动 loopAsk 轮询，届时 HID 通道将被独占
        await this.askDeviceKeys()
        // 只有火柴人设备的板载配置支持判定依赖当前固件版本，
        // 因此仅对该系列在配置初始化前等待一次版本读取，避免把等待成本扩散到所有设备。
        // checkFirmwareUpdate() 里还包含远端 HTTP 检查，不需要 await，继续 fire-and-forget。
        if (specialCheck(speciaBehiver.DevicesOfHuoChaiRen, res.ID)) {
          try {
            await firmware.askFirmwareVersion()
          } catch (error) {
            console.warn('askFirmwareVersion failed before askConfigData', error)
          }
        }
        DeviceManager.instance.setCurrentDevice(res.ID)
        firmware.checkFirmwareUpdate()
        if (DeviceManager.instance.state.currentDevice.IsSupportMagnet) {
          calibrationHandler.initCalibration()
        }
        // HID 校准页显示时，跳过其余 HID 操作，避免与校准 loopAsk 竞争协议队列
        // 校准结束后由 completePostCalibrationInit() 补充执行
        if (calibrationHandler.state.isShowHidCalibration) {
          return
        }
        await this.checkMer68SEFirstUse()
        await this.alignCurrentProfileWithOnboardConfig()
      }
      await this.askDeviceRelatedDataInfo()
      this.handleConfig()
      this.askConfigData()
      this.askCurrentDeviceState()
      // 初始化完成后才允许功能页触发 readSettings
      operate.resetSettingsRead()
    } catch (error) {
      console.error('askDeviceInfo failed:', error)
    }
  }

  private async checkMer68SEFirstUse() {
    if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMagnet)) {
      const key = `${STORAGE_KEYS.MER68SE_FIRST_USE}_${DeviceManager.instance.state.currentPid}`
      const isFirstUse = localStorage.getItem(key)
      if (!isFirstUse) {
        try {
          const isKeyModeOn = await setOrRead.read<boolean>(SpecificItem.SuperResponsiveMode, false)
          if (isKeyModeOn) {
            await setOrRead.write(SpecificItem.SuperResponsiveMode, false)
            console.log('MER68SE first use: Keyboard Mode Switch turned off')
          }
          localStorage.setItem(key, 'true')
        } catch (error) {
          console.error('checkMer68SEFirstUse error:', error)
        }
      }
    }
  }

  /** 校准结束后补充执行因 HID 校准页而延迟的设备初始化步骤 */
  async completePostCalibrationInit() {
    try {
      await this.checkMer68SEFirstUse()
      await this.alignCurrentProfileWithOnboardConfig()
      // askDeviceKeys 已在 initializeDevice 校准分支中提前执行，此处仅补充剩余步骤
      // 首次选择设备时不再立即 readSettings，延迟到用户切换至灯效/动作等功能页时再读取
      this.handleConfig()
      this.askConfigData()
      this.askCurrentDeviceState()
      // 校准后初始化完成，允许功能页触发 readSettings
      operate.resetSettingsRead()
    } catch (error) {
      console.error('completePostCalibrationInit failed:', error)
    }
  }

  // 读取设备固件信息，并检查是否需要更新
  // 读取设备按键的kk和vk
  // 首次选择设备时不再立即 readSettings，延迟到用户切换至灯效/动作等功能页时再读取
  private async askDeviceRelatedDataInfo() {
    // firmware.checkFirmwareUpdate()
    await this.askDeviceKeys()
  }

  private async alignCurrentProfileWithOnboardConfig() {
    if (DeviceManager.instance.state.driverProtocol !== DeviceProtocol.hid_web) {
      return
    }

    try {
      await this.getSocketService.askTypeAndData({
        Type: DriverSocketMsgType.MTOnboardCfgList,
        Data: { AlignCurrentProfile: true },
      } as DriverSocketMsg<{ AlignCurrentProfile: boolean }>)
    } catch (error) {
      console.warn('align current profile with onboard config failed', error)
    }
  }

  private async askDeviceKeys() {
    kks.value = await this.getSocketService.askType<{ [index: KK]: VK }>(DriverSocketMsgType.MTGetDefineKeys)
  }

  // 接受设备状态
  private async askCurrentDeviceState() {
    if (specialCheck(speciaBehiver.SupportGetDeviceStatus, DeviceManager.instance.state.currentPid) == false) {
      return
    }
    const res = await this.askType<ResDeviceState>(DriverSocketMsgType.MTDeviceState)
    if (res) {
      DeviceManager.instance.state.deviceStatus = res
    }
  }

  async askToSelect(pid: DevicePid) {
    const data = { Type: DriverSocketMsgType.MTSelectDevice, Data: pid } as DriverSocketMsg<any>
    return await this.getSocketService.askTypeAndData(data)
  }

  private handleConfig() {
    // TODO: 不应该多次handel，需修改
    configSet.handelChangeConfig()
    configSet.handelSwitchConfig()
  }

  private askConfigData() {
    configSet.getConfigRecommend()
    configSet.apiConfigList()
  }

  askDeviceVersion = (): Promise<string> => {
    return this.getSocketService.askType<string>(DriverSocketMsgType.MTDeviceVersion)
  }

  lanuchNcus() {
    return HelperService.instance.launchEEvision()
  }

  killDriverService() {
    this.exeDriver.killDriverService()
  }
}
